/**
 * The full universe of theme identifiers known to the app.
 *
 * Two of these — `sun` and `cosmos` — are *resolved* (computed) palettes and
 * never appear directly in the picker; they only surface when the user picks
 * `auto`, which the provider translates to one of them based on local time.
 * Persistence still allows them as historical/migration values so we don't
 * crash if an old build wrote one of them to storage.
 */
export type VisualThemeId = 'neutral' | 'varangian' | 'dark' | 'auto' | 'sun' | 'cosmos';

/**
 * Resolved palette identifier — `auto` is always translated to `sun` or
 * `cosmos` before reaching consumers. Use this type in sound helpers and
 * other modules that need the *actual* active palette, never `auto`.
 */
export type ResolvedVisualThemeId = 'neutral' | 'varangian' | 'dark' | 'sun' | 'cosmos';

/** Preset identifiers shown in the theme picker UI (order = display order). */
export const VISUAL_THEME_IDS: VisualThemeId[] = ['neutral', 'varangian', 'dark', 'auto'];

export const DEFAULT_VISUAL_THEME: VisualThemeId = 'neutral';

/**
 * Тема для того, кто ещё ничего не выбирал, — по схеме системы (DS-1).
 *
 * <p><b>Что было.</b> Приложение открывалось светлым `neutral` независимо от телефона.
 * Тёмная палитра при этом давно написана и рабочая — до неё просто нельзя было
 * добраться иначе, чем руками через настройки. Человек с тёмным телефоном получал
 * светлый мессенджер и решал, что тёмной темы нет.
 *
 * <p><b>Почему это не то же самое, что пресет `auto`.</b> `auto` — про часы: днём `sun`,
 * ночью `cosmos`, две авторские палитры. Смысл у него свой, и подменять его системной
 * схемой значило бы потерять фичу. Система отвечает только за <b>стартовое</b>
 * значение — то, с которого начинается пользователь без сохранённого выбора.
 *
 * <p>Любой сохранённый выбор, включая `auto`, эту функцию обходит: ручное решение
 * всегда старше системного умолчания.
 */
export function systemDefaultVisualTheme(
  /**
   * Схема от системы. Тип нарочно шире, чем `'light' | 'dark'`: React Native отдаёт ещё
   * и `'unspecified'` (Android до 10, и просто «система не сказала»), а на вебе схемы
   * может не быть вовсе. Всё, что не «тёмная», — светлая: это умолчание, а не догадка.
   */
  colorScheme: string | null | undefined,
): VisualThemeId {
  return colorScheme === 'dark' ? 'dark' : DEFAULT_VISUAL_THEME;
}

export function isVisualThemeId(v: string | null | undefined): v is VisualThemeId {
  return (
    v === 'neutral' ||
    v === 'varangian' ||
    v === 'dark' ||
    v === 'auto' ||
    v === 'sun' ||
    v === 'cosmos'
  );
}

/**
 * Local-hour boundary used by the `auto` theme. Sun palette is active for the
 * half-open interval [`AUTO_DAY_START_HOUR`, `AUTO_NIGHT_START_HOUR`); cosmos
 * is active otherwise. These boundaries are intentionally fixed (no DST math)
 * because the user's wall-clock perception of "day / night" already follows
 * local time — the OS gives us that for free via `Date#getHours()`.
 */
export const AUTO_DAY_START_HOUR = 6;
export const AUTO_NIGHT_START_HOUR = 19;

/** Resolve `auto` to its concrete palette for a given moment. Exported for tests. */
export function resolveAutoTheme(now: Date): 'sun' | 'cosmos' {
  const h = now.getHours();
  return h >= AUTO_DAY_START_HOUR && h < AUTO_NIGHT_START_HOUR ? 'sun' : 'cosmos';
}

/**
 * Milliseconds until the next sun/cosmos boundary crossing. Used to schedule a
 * single timer instead of polling — the auto theme only changes twice per day,
 * so per-minute interval timers would be wasteful on mobile (battery).
 *
 * The returned delay is clamped to ≥ 1s to avoid tight reschedule loops if the
 * device clock is exactly on the boundary, and to ≤ 12h as a defensive upper
 * bound against pathological clock skew during DST transitions.
 */
export function msUntilNextAutoBoundary(now: Date): number {
  const next = new Date(now);
  const h = now.getHours();
  const isDay = h >= AUTO_DAY_START_HOUR && h < AUTO_NIGHT_START_HOUR;
  next.setMinutes(0, 0, 0);
  if (isDay) {
    next.setHours(AUTO_NIGHT_START_HOUR);
  } else if (h < AUTO_DAY_START_HOUR) {
    next.setHours(AUTO_DAY_START_HOUR);
  } else {
    next.setDate(next.getDate() + 1);
    next.setHours(AUTO_DAY_START_HOUR);
  }
  const delta = next.getTime() - now.getTime();
  if (!Number.isFinite(delta) || delta < 1000) return 1000;
  return Math.min(delta, 12 * 60 * 60 * 1000);
}
